import { useEffect, useRef, useState, type ReactNode } from 'react'

import { ArrowLeft, XClose } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { Modal, ModalTitle } from '@/components/modal/Modal'
import { ModalContainer } from '@/components/modal/ModalContainer'
import { useViewportSizeInPixels } from '@/hooks/use-viewport-size-in-pixels'

import { SETTINGS_SECTIONS } from './settings-modal-sections'
import { SettingsNav } from './SettingsNav'
import { SettingsNavList } from './SettingsNavList'
import type { SettingsSection, SettingsSectionId } from './settings-modal-types'

type SettingsModalProps = {
  /** Whether the modal is open (controlled). */
  open: boolean
  /** Fired when the open state should change (close button, Escape, outside click). */
  onOpenChange: (open: boolean) => void
  /** Sections to render in the left nav. Defaults to {@link SETTINGS_SECTIONS}. */
  sections?: SettingsSection[]
  /** Currently active section. */
  activeSectionId: SettingsSectionId
  /** Fired when the user selects a different section. */
  onSectionChange: (id: SettingsSectionId) => void
  /** Header label for a nav group (e.g. "Personal settings", the org name). Passed opaque so the shell stays app-agnostic. */
  groupLabel?: (group: number) => string | null
  /** Content for the active section, rendered in the scrollable content area. */
  children: ReactNode
  /** When true, mobile opens directly on the section content instead of the nav list.
   *  Set by the container when a specific section was explicitly requested. */
  openOnSection?: boolean
  /** The section whose content is on screen. Null while closed or while the mobile list is showing. */
  onVisibleSectionChange?: (sectionId: SettingsSectionId | null) => void
}

/** Matches Tailwind's `sm` breakpoint (min-width 640px) so the JS layout switch and the `max-sm:` sizing flip at the same width. */
const MOBILE_MAX_WIDTH = 640

/**
 * Responsive settings modal shell.
 *
 * - **Desktop/tablet:** a left section nav alongside a scrollable content column (1200×720).
 * - **Mobile:** a full-screen, single-column drill-down — a nav list that opens a section view with a
 *   back button, matching the mobile design and the repo's full-screen-on-mobile modal pattern.
 *
 * Presentational and state-agnostic about the app — it owns layout, the close affordance, and section
 * routing via props (plus local mobile view state), but knows nothing about the user, org, or the
 * panels it hosts. Pass the active section's content as `children`.
 */
export function SettingsModal({
  open,
  onOpenChange,
  sections = SETTINGS_SECTIONS,
  activeSectionId,
  onSectionChange,
  groupLabel,
  children,
  openOnSection,
  onVisibleSectionChange,
}: SettingsModalProps) {
  const { width } = useViewportSizeInPixels()
  const isMobile = width < MOBILE_MAX_WIDTH
  const activeSection = sections.find((section) => section.id === activeSectionId)

  // Mobile is a two-level drill-down: the nav list, then a section view with a back button.
  const [mobileView, setMobileView] = useState<'nav' | 'section'>('nav')
  const layoutGenerationRef = useRef(0)
  const reportedLayoutGenerationRef = useRef(0)

  // Open on the nav list by default; jump to section content when a specific section was
  // explicitly targeted (redirect routes, upsell dispatches).
  useEffect(() => {
    if (open) setMobileView(openOnSection ? 'section' : 'nav')
    layoutGenerationRef.current += 1
  }, [open, openOnSection])

  useEffect(() => {
    if (!onVisibleSectionChange) return
    if (!open) {
      onVisibleSectionChange(null)
      reportedLayoutGenerationRef.current = layoutGenerationRef.current
      return
    }
    if (!isMobile) {
      onVisibleSectionChange(activeSectionId)
      return
    }
    // mobileView still has the previous drill-down until the layout effect's update paints.
    // Skip that frame so a reopen does not report the section the user already left.
    const settledView = openOnSection ? 'section' : 'nav'
    const layoutPending = reportedLayoutGenerationRef.current !== layoutGenerationRef.current && mobileView !== settledView
    if (layoutPending) return
    reportedLayoutGenerationRef.current = layoutGenerationRef.current
    onVisibleSectionChange(mobileView === 'section' ? activeSectionId : null)
  }, [open, isMobile, mobileView, openOnSection, activeSectionId, onVisibleSectionChange])

  function handleMobileSelect(id: SettingsSectionId) {
    onSectionChange(id)
    setMobileView('section')
  }

  const closeButton = <IconButton variant="plain" color="neutral" icon={<XClose />} aria-label="Close settings" onClick={() => onOpenChange(false)} />

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContainer
        data-ds-component="SettingsModal"
        // No ModalDescription is rendered, so suppress Radix's auto-generated id to avoid its
        // "Missing Description or aria-describedby" console warning.
        aria-describedby={undefined}
        // Desktop: fixed 1200×720, clamped to the viewport (ModalContainer's base w-[calc(100%-2rem)]
        // and max-h-[85dvh] still apply). `max-w-300` sits at the base layer so it overrides the DS
        // `width="md"` preset (max-w-[500px]); below `sm` the base width governs, so the shell truly
        // fills the screen. Mobile full-screen height is scoped with `max-sm:` (repo pattern).
        className="h-180 max-w-300 max-sm:h-[calc(100dvh-2rem)] max-sm:max-h-none"
      >
        {/* Radix requires an accessible title; the visible mobile header below is marked aria-hidden. */}
        <ModalTitle className="sr-only">{activeSection?.label ?? 'Settings'}</ModalTitle>

        {isMobile ? (
          <div className="flex h-full min-h-0 flex-col">
            <div className="flex h-14 shrink-0 items-center gap-1 border-b border-border-default px-2">
              {mobileView === 'section' && (
                <IconButton
                  variant="plain"
                  color="neutral"
                  // Figma sizes the back glyph at 12px (size-3); `!` overrides IconButton's default icon size.
                  icon={<ArrowLeft className="size-3!" />}
                  aria-label="Back to settings"
                  onClick={() => setMobileView('nav')}
                />
              )}
              <h2 aria-hidden="true" className="min-w-0 flex-1 px-2 font-rethink-sans text-lg font-semibold text-text-primary">
                Settings
              </h2>
              {closeButton}
            </div>

            {mobileView === 'nav' ? (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <SettingsNavList sections={sections} onSelect={handleMobileSelect} groupLabel={groupLabel} />
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>
            )}
          </div>
        ) : (
          <div className="relative flex h-full min-h-0">
            <SettingsNav sections={sections} activeSectionId={activeSectionId} onSectionChange={onSectionChange} groupLabel={groupLabel} />

            {/* pt-6 (24px) gives the section title the same top offset as the side-nav's top label. */}
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-12.5 pb-4 pt-6">
              <div className="mx-auto flex h-full w-full max-w-225 flex-col">{children}</div>
            </div>

            <div className="absolute right-1.5 top-1.5 z-10">{closeButton}</div>
          </div>
        )}
      </ModalContainer>
    </Modal>
  )
}
